import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'Natasha Simone';
const SITE_URL = 'https://iamnatashasimone.com';
const DEFAULT_IMAGE = `${SITE_URL}/images/natasha-og.jpg`;

const PAGE_SEO = {
  '/': {
    title: 'Natasha Simone | Beauty Industry Master Coach & Author',
    description: 'Natasha Simone trains beauty professionals and future leaders worldwide. Master coaching, speaking, education, and business growth for the beauty industry.',
    keywords: 'beauty industry coach, beauty business consultant, salon business coach, beauty entrepreneur, Natasha Simone, author, speaker, personal development',
  },
  '/business': {
    title: 'For Business | Natasha Simone — Beauty Industry Coaching & Consulting',
    description: 'Transform your beauty business with Natasha Simone. Expert coaching, salon management systems, team workshops, and proven strategies to scale your beauty enterprise.',
    keywords: 'beauty business coaching, salon consulting, beauty industry training, salon owner coach, team workshops, business growth strategies, DIS program',
  },
  '/personal': {
    title: 'Personal Growth | Natasha Simone — Mindset Coaching & Author',
    description: 'Discover personal transformation with Natasha Simone. Mindset coaching, self-development programs, The Beauty of You book, and empowering experiences for women.',
    keywords: 'personal development coach, mindset coaching, self-improvement, The Beauty of You, women empowerment, personal growth speaker, confidence coaching',
  },
  '/events': {
    title: 'Events & Experiences | Natasha Simone — Retreats, Workshops & Summits',
    description: 'Join Natasha Simone at transformative events — leadership summits, beauty industry workshops, personal growth retreats, and the Father-Daughter Gala.',
    keywords: 'beauty industry events, leadership summit, personal growth retreat, salon workshops, women empowerment events, Father-Daughter Gala',
  },
  '/privacy': {
    title: 'Privacy Policy | Natasha Simone',
    description: 'Privacy policy for iamnatashasimone.com. Learn how we collect, use, and protect your personal information.',
    keywords: '',
  },
  '/terms': {
    title: 'Terms of Service | Natasha Simone',
    description: 'Terms of service for iamnatashasimone.com. Read the terms and conditions for using our website and services.',
    keywords: '',
  },
};

export const SEOHead = ({ path, title, description, keywords, image, type = 'website', noindex = false }) => {
  const defaults = PAGE_SEO[path] || PAGE_SEO['/'];
  const pageTitle = title || defaults.title;
  const pageDescription = description || defaults.description;
  const pageKeywords = keywords || defaults.keywords;
  const pageImage = image || DEFAULT_IMAGE;
  const canonicalUrl = `${SITE_URL}${path || '/'}`;

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      {pageKeywords && <meta name="keywords" content={pageKeywords} />}
      <link rel="canonical" href={canonicalUrl} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImage} />

      {/* Additional SEO */}
      <meta name="author" content="Natasha Simone" />
    </Helmet>
  );
};

export const PersonStructuredData = () => (
  <Helmet>
    <script type="application/ld+json">
      {JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Person',
        name: 'Natasha Simone',
        url: SITE_URL,
        image: DEFAULT_IMAGE,
        jobTitle: 'Beauty Industry Master Coach & Author',
        description: 'Natasha Simone trains beauty professionals and future leaders worldwide through coaching, speaking, education, and business growth programs.',
        sameAs: [],
        knowsAbout: [
          'Beauty Industry',
          'Business Coaching',
          'Personal Development',
          'Leadership',
          'Salon Management',
          'Public Speaking',
        ],
      })}
    </script>
  </Helmet>
);

export const BusinessStructuredData = () => (
  <Helmet>
    <script type="application/ld+json">
      {JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'ProfessionalService',
        name: 'Natasha Simone Coaching',
        url: SITE_URL,
        image: DEFAULT_IMAGE,
        description: 'Beauty industry coaching, consulting, and professional development services.',
        priceRange: '$$',
        areaServed: { '@type': 'Place', name: 'Worldwide' },
        serviceType: [
          'Business Coaching',
          'Salon Consulting',
          'Team Workshops',
          'Speaking Engagements',
          'Personal Development Coaching',
        ],
      })}
    </script>
  </Helmet>
);

export const WebsiteStructuredData = () => (
  <Helmet>
    <script type="application/ld+json">
      {JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: SITE_NAME,
        url: SITE_URL,
        description: 'Natasha Simone trains beauty professionals and future leaders worldwide.',
        publisher: {
          '@type': 'Person',
          name: 'Natasha Simone',
        },
      })}
    </script>
  </Helmet>
);

export const BookStructuredData = () => (
  <Helmet>
    <script type="application/ld+json">
      {JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Book',
        name: 'Dear Beautiful Girl',
        description: 'A Guide to Confidence, Purpose, and Self-Love. Written for girls and young women ready to step into their greatness.',
        author: {
          '@type': 'Person',
          name: 'Natasha Simone',
          url: SITE_URL,
        },
        image: `${SITE_URL}/images/dear-beautiful-girl-mockup-1.jpg`,
        bookFormat: 'https://schema.org/Paperback',
        inLanguage: 'en',
        publisher: {
          '@type': 'Organization',
          name: 'Yellow Mango Production',
        },
      })}
    </script>
  </Helmet>
);


export const EventStructuredData = ({ events = [] }) => {
  if (!events.length) return null;
  const items = events.map((e) => ({
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: e.title,
    startDate: e.date,
    endDate: e.date,
    location: {
      '@type': 'Place',
      name: e.location || 'TBA',
      address: {
        '@type': 'PostalAddress',
        name: e.location || 'TBA',
      },
    },
    description: e.description,
    organizer: {
      '@type': 'Person',
      name: 'Natasha Simone',
      url: SITE_URL,
    },
    performer: {
      '@type': 'Person',
      name: 'Natasha Simone',
      url: SITE_URL,
    },
    offers: {
      '@type': 'Offer',
      url: e.external_url || `${SITE_URL}/events`,
      availability: 'https://schema.org/InStock',
    },
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    ...(e.image && { image: e.image.startsWith('http') ? e.image : `${SITE_URL}${e.image}` }),
    ...(e.external_url && { url: e.external_url }),
  }));

  return (
    <Helmet>
      {items.map((item, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(item)}
        </script>
      ))}
    </Helmet>
  );
};
